import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@/components/ui/icon';
import { TONE_STYLES, useToastStore, type Toast } from '@/components/ui/toast-store';
import { cn } from '@/lib/cn';

/** Mounted once, near the root. */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);

  if (toasts.length === 0) {
    return null;
  }

  return createPortal(
    <div
      /* `polite` so a success message never interrupts what a screen-reader
         user is currently reading. */
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItem({ toast: item }: { toast: Toast }) {
  const dismiss = useToastStore((state) => state.dismiss);
  const { icon, className } = TONE_STYLES[item.tone];

  /* A frame at the starting offset, then the class flips and the browser has
     something to transition from - see usePresence for the same trick. */
  const [shown, setShown] = useState(false);

  useEffect(() => {
    /* Two frames, not one: the first paints the offset position, the second
       flips. Within a single frame the browser only ever sees the final value
       and drops the transition. */
    let second = 0;

    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        setShown(true);
      });
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  useEffect(() => {
    if (item.durationMs <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      dismiss(item.id);
    }, item.durationMs);

    return () => {
      clearTimeout(timer);
    };
  }, [item.id, item.durationMs, dismiss]);

  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex items-start gap-2.5 rounded-md border px-3 py-2.5',
        'shadow-[0_4px_16px_rgba(10,15,30,0.10)]',
        /* Rises from below the stack, which reads as arriving rather than
           being switched on - and makes a second toast visibly a second one. */
        'transition-[opacity,transform] duration-panel ease-enter',
        shown ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        className,
      )}
    >
      <Icon name={icon} size={15} className="mt-0.5 shrink-0" />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{item.title}</p>
        {item.description && (
          <p className="mt-0.5 text-xs break-words text-content-muted">{item.description}</p>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          dismiss(item.id);
        }}
        aria-label="Dismiss notification"
        className="shrink-0 rounded-sm p-0.5 opacity-70 hover:bg-surface-muted hover:opacity-100"
      >
        <Icon name="close" size={13} />
      </button>
    </div>
  );
}
